import axios from 'axios';
import attachAuthToken from '../axiosConfig';
import { JobCompleto, JobResumo } from '../../components/sync-painel/types';
import { clienteDoLogGeral } from '../logGeral/clienteDoLogGeral';

/**
 * Painel de Sincronização FIPE (fipe-atual) — o espelho local da tabela FIPE que alimenta
 * a seleção de marca/modelo/ano no cadastro de veículos. Mesmo desenho dos outros módulos
 * satélites (Portal de Cotações, Cilia, Base de Dados): o módulo só existe na UI quando a
 * env aponta para o backend dele, e as chamadas levam o JWT do SOAC (o backend da FIPE
 * valida o token e exige ADMIN/TI — ADR 0005 daquele serviço).
 *
 * ⚠️ Sem default para `localhost`: um fallback faria o módulo aparecer em produção
 * apontando para a máquina de quem abriu a tela, e o gate viraria decoração.
 *
 * ⚠️ Todas as rotas ficam sob o prefixo global `/api` do fipe-atual.
 */
export const FIPE_SYNC_HABILITADO = Boolean(process.env.REACT_APP_FIPE_URL);

const BASE_URL = process.env.REACT_APP_FIPE_URL ?? '';

const api = axios.create({ baseURL: BASE_URL });
attachAuthToken(api);

/** Log geral deste serviço (transversal/at-91), lido pela tela Logs do sistema do SOAC. */
export const logGeralDaFipe = clienteDoLogGeral(api);

// ---------------------------------------------------------------------------
// Tipos próprios do painel da FIPE (os de job vêm do módulo compartilhado)
// ---------------------------------------------------------------------------

export type { JobResumo, JobCompleto, SyncJobStatus, EtapaResult } from '../../components/sync-painel/types';

/** Fase em que a pendência nasceu — define o escopo do reprocessamento. */
export type EtapaFalha = 'modelos' | 'anos' | 'veiculos';

export interface EscopoFalha {
  marcaCode: string;
  marcaNome: string;
  vehicleType: string;
  modeloCode?: string;
  modeloNome?: string;
  anoCode?: string;
  anoNome?: string;
}

export interface FalhaFipeItem {
  id: string;
  etapa: EtapaFalha;
  escopo: EscopoFalha;
  motivo: string;
  criadoEm: string;
  resolvidoEm: string | null;
  obsoleta: boolean;
}

export interface OverviewFipe {
  agora: string;
  ultimoFull: JobResumo | null;
  ultimoRecover: JobResumo | null;
  falhasPendentes: {
    total: number;
    porEtapa: Record<string, number>;
    itens: FalhaFipeItem[];
  };
  /** Estado do espelho local: o que a Recepção enxerga hoje no cadastro de veículos. */
  dados: {
    marcas: number;
    modelos: number;
    anos: number;
    veiculos: number;
    referenceMonth: string | null;
  };
  crons: Array<{ quando: string; oque: string }>;
}

export interface DisparoResposta {
  message: string;
  jobId: string;
  statusUrl: string;
}

/** 409: já existe execução em andamento — o body diz qual. */
export interface ConflitoResposta {
  message: string;
  jobId: string;
  tipo?: string;
  statusUrl: string;
}

export interface CancelamentoResposta {
  message: string;
  jobId?: string;
}

/** Rótulos curtos por tipo de job, para a coluna do histórico. */
export const ROTULO_TIPO_FIPE: Record<string, string> = {
  full: 'Sincronização',
  recover: 'Reprocessamento',
};

/** Títulos longos, para o cabeçalho do modal de detalhe. */
export const TITULO_TIPO_FIPE: Record<string, string> = {
  full: 'Sincronização completa',
  recover: 'Reprocessamento de pendências',
};

export const OPCOES_TIPO_FIPE = [
  { valor: 'full', rotulo: 'Sincronização completa' },
  { valor: 'recover', rotulo: 'Reprocessamento' },
];

/** Rótulo da fase em que a pendência nasceu. */
export const ROTULO_ETAPA_FALHA: Record<string, string> = {
  modelos: 'Modelos',
  anos: 'Anos',
  veiculos: 'Preço FIPE',
};

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export const fetchOverviewFipe = async (): Promise<OverviewFipe> => {
  const { data } = await api.get<OverviewFipe>('/api/sync/overview');
  return data;
};

export const fetchJobsFipe = async (params: {
  tipo?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ total: number; page: number; pageSize: number; jobs: JobResumo[] }> => {
  const { data } = await api.get('/api/sync/jobs', { params });
  return data;
};

export const fetchJobDetalheFipe = async (id: string): Promise<JobCompleto> => {
  const { data } = await api.get<JobCompleto>(`/api/sync/jobs/${id}`);
  return data;
};

export const fetchFalhasFipe = async (params: {
  etapa?: string;
  pendentes?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{ total: number; page: number; pageSize: number; itens: FalhaFipeItem[] }> => {
  const { data } = await api.get('/api/sync/falhas', {
    params: { ...params, pendentes: params.pendentes ? 'true' : undefined },
  });
  return data;
};

export const fetchFalhaDetalheFipe = async (id: string): Promise<FalhaFipeItem> => {
  const { data } = await api.get<FalhaFipeItem>(`/api/sync/falhas/${id}`);
  return data;
};

// ---------------------------------------------------------------------------
// Disparos (jobs em background — 202 com jobId; 409 quando já há execução)
// ---------------------------------------------------------------------------

export const dispararSyncFipe = async (): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/api/sync/trigger');
  return data;
};

export const cancelarSyncFipe = async (): Promise<CancelamentoResposta> => {
  const { data } = await api.post<CancelamentoResposta>('/api/sync/cancel');
  return data;
};

/**
 * Reprocessa pendências. Sem `falhaIds`, reprocessa todas as pendentes.
 *
 * ⚠️ O reprocessamento é escopado e em CASCATA: recuperar uma marca refaz também os anos
 * e os preços dela. E jamais executa a fase de Marcas, que é a única com reconciliação
 * global (ADR 0005 do fipe-atual).
 */
export const reprocessarFipe = async (falhaIds?: string[]): Promise<DisparoResposta> => {
  const { data } = await api.post<DisparoResposta>('/api/sync/recover', falhaIds?.length ? { falhaIds } : undefined);
  return data;
};
